import pkg from "../../package.json" with { type: "json" };

const SEMVER = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;

function bakedPortalVersion(): string {
  const fromBuild = (import.meta.env.VITE_PORTAL_VERSION ?? "").trim();
  return SEMVER.test(fromBuild) ? fromBuild : pkg.version;
}

export const PORTAL_VERSION: string = bakedPortalVersion();
export const API_VERSION_STUB = "0.1.0";
const SHA = /^[0-9a-f]{7,40}$/;
const SHA256 = /^[0-9a-f]{64}$/;
const TAG = /^[A-Za-z0-9_][A-Za-z0-9_.-]{0,63}$/;

export type MenuStamp = {
  portalVersion: string;
  apiVersion: string;
  live: string | null;
};

export function semverOrStub(value: string | null | undefined): string {
  const text = value?.trim() ?? "";
  return SEMVER.test(text) ? text : API_VERSION_STUB;
}

export function revisionTag(raw: string | null | undefined): string | null {
  const text = (raw ?? "").trim();
  if (text.length === 0 || text.length > 512) return null;
  const marker = "@sha256:";
  const digestAt = text.indexOf(marker);
  if (digestAt >= 0) {
    const hex = text.slice(digestAt + marker.length);
    return SHA256.test(hex) ? hex : null;
  }
  const colon = text.lastIndexOf(":");
  if (colon >= 0) {
    if (!text.includes("/")) return null;
    return tag(text.slice(colon + 1));
  }
  if (SHA.test(text)) return text;
  return tag(text);
}

export async function loadMenuStamp(apiBase: string, signal: AbortSignal): Promise<MenuStamp> {
  let apiVersion = API_VERSION_STUB;
  let apiRevision: string | null = null;
  try {
    const response = await fetch(`${apiBase}/health`, {
      signal,
      headers: { Accept: "application/json" },
    });
    if (response.ok) {
      const type = response.headers.get("content-type") ?? "";
      if (type.includes("application/json")) {
        const body = (await response.json()) as { version?: unknown; revision?: unknown };
        apiVersion = semverOrStub(typeof body.version === "string" ? body.version : null);
        apiRevision = revisionTag(typeof body.revision === "string" ? body.revision : null);
      } else {
        apiVersion = semverOrStub(await response.text());
      }
    }
  } catch {
    apiVersion = API_VERSION_STUB;
    apiRevision = null;
  }

  let portalRevision: string | null = null;
  try {
    const stamp = await fetch("/stamp.json", { signal, cache: "no-store" });
    if (stamp.ok) {
      const body = (await stamp.json()) as { revision?: unknown };
      portalRevision = revisionTag(typeof body.revision === "string" ? body.revision : null);
    }
  } catch {
    portalRevision = null;
  }

  return {
    portalVersion: PORTAL_VERSION,
    apiVersion,
    live: formatLive(portalRevision, apiRevision),
  };
}

function formatLive(portal: string | null, api: string | null): string | null {
  if (portal !== null && api !== null) return `Live portal ${portal} · api ${api}`;
  if (portal !== null) return `Live portal ${portal}`;
  if (api !== null) return `Live api ${api}`;
  return null;
}

function tag(value: string): string | null {
  return TAG.test(value) ? value : null;
}
